import React from 'react';

import { generationSimilarQuestions, importFAQsService } from '@mygpt/app/faqs/services';
import { addFAQsService, batchAddFAQsService } from '@mygpt/app/faqs/services/add';
import { batchDeleteFAQsService, deleteFAQsService } from '@mygpt/app/faqs/services/delete';
import { faqsDetail } from '@mygpt/app/faqs/services/detail';
import { updateFAQsService } from '@mygpt/app/faqs/services/update';
import { faqsStore } from '@mygpt/app/faqs/store';

export const useFAQsMethods = (datasetId: string) => {
  const [loading, setLoading] = React.useState(false);

  const add = React.useCallback(
    async (params: Parameters<typeof addFAQsService>[0]) => {
      setLoading(true);
      return addFAQsService(params)
        .then((res) => {
          const data = faqsStore.getState().records[datasetId] ?? [];
          const newData = [res.data, ...data];
          faqsStore.setState({
            records: {
              [datasetId]: newData,
            },
            cacheTotal: {
              [datasetId]: (faqsStore.getState().cacheTotal[datasetId] ?? 0) + 1,
            },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [datasetId]
  );

  const batchAdd = React.useCallback(
    async (params: Parameters<typeof batchAddFAQsService>[0]) => {
      setLoading(true);
      return batchAddFAQsService(params)
        .then((res) => {
          const data = faqsStore.getState().records[datasetId] ?? [];
          const newData = data.concat();
          newData.unshift(...res.data.faqs);
          faqsStore.setState({
            records: {
              [datasetId]: newData,
            },
            cacheTotal: {
              [datasetId]: (faqsStore.getState().cacheTotal[datasetId] ?? 0) + res.data.length,
            },
          });
        })
        .finally(() => {
          setLoading(false);
        })
        .catch((err) => {});
    },
    [datasetId]
  );

  const batchDelete = React.useCallback(
    async (params: Parameters<typeof batchDeleteFAQsService>[0]) => {
      setLoading(true);
      return batchDeleteFAQsService(params)
        .then(() => {
          const data = faqsStore.getState().records[datasetId] ?? [];
          const newData = data.concat();
          params.faqsIds.forEach((id) => {
            const index = newData.findIndex((item) => item.id === id);
            if (index > -1) {
              newData.splice(index, 1);
            }
          });
          faqsStore.setState({
            records: {
              [datasetId]: newData,
            },
            cacheTotal: {
              [datasetId]: (faqsStore.getState().cacheTotal[datasetId] ?? 0) - params.faqsIds.length,
            },
          });
        })
        .finally(() => {
          setLoading(false);
        })
        .catch((err) => {});
    },
    [datasetId]
  );

  const update = React.useCallback(
    async (params: Parameters<typeof updateFAQsService>[0]) => {
      setLoading(true);
      return updateFAQsService(params)
        .then((res) => {
          const data = faqsStore.getState().records[datasetId] ?? [];
          const newData = data.concat();
          const item = newData.find((item) => item.id === res.data.id);
          if (item) {
            Object.assign(item, res.data);
            faqsStore.setState({
              records: {
                [datasetId]: newData,
              },
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [datasetId]
  );

  const _delete = React.useCallback(
    async (params: Parameters<typeof deleteFAQsService>[0]) => {
      setLoading(true);
      return deleteFAQsService(params)
        .then((res) => {
          const data = faqsStore.getState().records[datasetId] ?? [];
          const newData = data.concat();
          const index = newData.findIndex((item) => item.id === params.faqsId);
          if (index > -1) {
            newData.splice(index, 1);
            faqsStore.setState({
              records: {
                [datasetId]: newData,
              },
              cacheTotal: {
                [datasetId]: (faqsStore.getState().cacheTotal[datasetId] ?? 0) - 1,
              },
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [datasetId]
  );

  const _import = React.useCallback(async (params: Parameters<typeof importFAQsService>[0]) => {
    setLoading(true);
    return importFAQsService(params).finally(() => {
      setLoading(false);
    });
  }, []);

  return {
    loading,
    addFAQs: add,
    batchAddFAQs: batchAdd,
    updateFAQs: update,
    deleteFAQs: _delete,
    batchDeleteFAQs: batchDelete,
    importFAQs: _import,
  };
};

export const useNewBotFAQsMethods = () => {
  const batchAdd = React.useCallback(async (params: Parameters<typeof batchAddFAQsService>[0]) => {
    const datasetId = params.datasetId;
    return batchAddFAQsService(params).then((res) => {
      const data = faqsStore.getState().records[datasetId] ?? [];
      const newData = data.concat();
      newData.unshift(...res.data.faqs);
      faqsStore.setState({
        records: {
          [datasetId]: newData,
        },
        cacheTotal: {
          [datasetId]: (faqsStore.getState().cacheTotal[datasetId] ?? 0) + res.data.length,
        },
      });
    });
  }, []);

  return {
    NewBotBatchAddFAQs: batchAdd,
  };
};

export const useNotIdFAQsMethods = () => {
  const [loading, setLoading] = React.useState(false);

  const add = React.useCallback(async (params: Parameters<typeof addFAQsService>[0]) => {
    setLoading(true);
    const datasetId = params.datasetId;
    return addFAQsService(params)
      .then((res) => {
        const data = faqsStore.getState().records[datasetId] ?? [];
        const newData = [res.data, ...data];
        faqsStore.setState({
          records: {
            [datasetId]: newData,
          },
          cacheTotal: {
            [datasetId]: (faqsStore.getState().cacheTotal[datasetId] ?? 0) + 1,
          },
        });
        return res.data;
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const update = React.useCallback(async (params: Parameters<typeof updateFAQsService>[0]) => {
    setLoading(true);
    const datasetId = params.datasetId;
    return updateFAQsService(params)
      .then((res) => {
        const data = faqsStore.getState().records[datasetId] ?? [];
        const newData = data.concat();
        const item = newData.find((item) => item.id === res.data.id);
        if (item) {
          Object.assign(item, res.data);
          faqsStore.setState({
            records: {
              [datasetId]: newData,
            },
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const detail = React.useCallback(async (params: Parameters<typeof faqsDetail>[0]) => {
    return faqsDetail(params)
      .then((res) => {
        return res.data;
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return {
    loading,
    addFAQs: add,
    updateFAQs: update,
    detailFAQ: detail,
  };
};

export const useFAQsQuestionsMethods = () => {
  const [loading, setLoading] = React.useState(false);

  const generateSimilarQuestions = React.useCallback(
    async (params: Parameters<typeof generationSimilarQuestions>[0]) => {
      setLoading(true);
      return generationSimilarQuestions(params)
        .then((res) => {
          return res.data;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );

  return {
    loading,
    generateSimilarQuestions,
  };
};
